import { Injectable } from '@angular/core';
import * as THREE from 'three';
import * as Cesium from 'cesium';
import { firstValueFrom, of } from 'rxjs';
import { BuildingModelAssetsService } from './building-model-assets.service';
import { BuildingModelAssetBundle } from '../models/building-model-assets.model';

export interface LoadGlbOnCesiumContext {
  viewer: any;
  floorMapEntity: any;
  assetBundle: BuildingModelAssetBundle | null;
  assetResolutionKey: string | null;
}

/**
 * GLB-on-Cesium loading for the floor map, extracted from FloorMapComponent.
 */
@Injectable({
  providedIn: 'root',
})
export class BlenderFloorMapService {
  private cesiumGlbModel: Cesium.Entity | null = null;
  private activeAssetBundle: BuildingModelAssetBundle | null = null;
  private isGlbLoading = false;
  private loadedGlbUrl: string | null = null;

  constructor(private buildingModelAssets: BuildingModelAssetsService) {}

  getActiveAssetBundle(): BuildingModelAssetBundle | null {
    return this.activeAssetBundle;
  }

  isGlbModelLoaded(): boolean {
    return !!this.cesiumGlbModel;
  }

  resolveAndLoadGlbOnCesium(context: LoadGlbOnCesiumContext): void {
    if (this.isGlbLoading) {
      return;
    }

    const source$ =
      context.assetBundle != null
        ? of(context.assetBundle)
        : this.buildingModelAssets.getAssetBundle(context.assetResolutionKey ?? undefined);

    firstValueFrom(source$)
      .then((bundle: BuildingModelAssetBundle) => {
        this.activeAssetBundle = bundle;
        if (!context.viewer || !bundle?.glbUrl) {
          return;
        }

        let glbUrl = bundle.glbUrl;
        if (bundle.floors && bundle.floors.length > 0 && context.floorMapEntity) {
          const targetPerimeterId = String(context.floorMapEntity.perimeterId || context.floorMapEntity.id || '');
          const targetFloorNumber = context.floorMapEntity.floorNumber;

          const matchedFloor = bundle.floors.find((f: any) =>
            (f.id && String(f.id) === targetPerimeterId) ||
            (f.token && String(f.token) === targetPerimeterId) ||
            (f.number !== undefined && f.number === targetFloorNumber)
          );

          if (matchedFloor && matchedFloor.url) {
            glbUrl = matchedFloor.url;
          }
        }

        if (this.loadedGlbUrl === glbUrl && this.cesiumGlbModel) {
          return;
        }

        if (this.cesiumGlbModel && context.viewer) {
          context.viewer.entities.remove(this.cesiumGlbModel);
          this.cesiumGlbModel = null;
        }

        this.isGlbLoading = true;
        return this.loadGlbOnCesium(context.viewer, context.floorMapEntity, glbUrl);
      })
      .catch((err: Error) => {
        console.error('Failed to load GLB on Cesium:', err);
        this.isGlbLoading = false;
      });
  }

  private loadGlbOnCesium(viewer: any, floorMapEntity: any, glbUrl: string): Promise<void> {
    if (!viewer) {
      this.isGlbLoading = false;
      return Promise.reject(new Error('No Cesium viewer'));
    }

    const locations = floorMapEntity?.locations;
    if (!Array.isArray(locations) || locations.length < 2) {
      this.isGlbLoading = false;
      return Promise.reject(new Error('No valid floor locations'));
    }

    const lats = locations.map((loc: any) => (Array.isArray(loc) ? loc[0] : loc.lat));
    const lons = locations.map((loc: any) => (Array.isArray(loc) ? loc[1] : loc.lng));
    const centerLat = lats.reduce((a: number, b: number) => a + b, 0) / lats.length;
    const centerLon = lons.reduce((a: number, b: number) => a + b, 0) / lons.length;
    const altitude = floorMapEntity?.extrudedHeight || 0;

    this.cesiumGlbModel = viewer.entities.add({
      name: 'building-glb-model',
      position: Cesium.Cartesian3.fromDegrees(centerLon, centerLat, altitude),
      model: {
        uri: glbUrl,
        minimumPixelSize: 16,
        maximumScale: 2000,
        scale: 1.0,
      },
    });

    setTimeout(() => {
      this.loadedGlbUrl = glbUrl;
      this.isGlbLoading = false;
      viewer?.scene.requestRender();
    }, 1000);

    viewer.scene.requestRender();
    return Promise.resolve();
  }

  cleanupGlbModel(viewer: any): void {
    if (this.cesiumGlbModel && viewer) {
      viewer.entities.remove(this.cesiumGlbModel);
      this.cesiumGlbModel = null;
    }
    this.loadedGlbUrl = null;
    this.isGlbLoading = false;
  }

  /**
   * Setup Three.js scene lighting specifically for Blender GLB Floor View (AFTER soft lighting setup).
   */
  setupBlenderLighting(
    scene: THREE.Scene | null,
    lightsGroup: THREE.Group
  ): void {
    if (!scene) return;

    if (!scene.children.includes(lightsGroup)) {
      scene.add(lightsGroup);
    }
    lightsGroup.clear();

    lightsGroup.add(new THREE.AmbientLight(0xffffff, 2.0));

    const dir = new THREE.DirectionalLight(0xffffff, 1.8);
    dir.position.set(100, 200, 100);
    lightsGroup.add(dir);
  }
}
